import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLines, Reveal, ClipImage, EASE } from "./Reveal";
import { GrainButton } from "./GrainButton";
import { scrollToId, openInquiry } from "../lib/scroll";
import { TIMELINE, IMG } from "../data/content";

export const OurStory = () => {
  const [active, setActive] = useState(0);

  return (
    <section id="about" data-testid="our-story-section" className="px-6 sm:px-10 lg:px-16 py-16 sm:py-36 max-w-[1700px] mx-auto">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-6">
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.28em] text-ember mb-5">SINCE 1972</p>
          <MaskLines
            as="h2"
            lines={[<>Five decades. <span className="font-serif italic font-normal">One standard.</span></>]}
            className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-3xl sm:text-5xl lg:text-[4rem] text-charcoal"
          />
          <div className="mt-10 space-y-6 max-w-xl">
            <Reveal><p className="text-base sm:text-lg text-charcoal/75 leading-relaxed">In November 1972, Nagel Landscaping began in an old two-bedroom ranch house on five acres in Modesto, California.</p></Reveal>
            <Reveal delay={0.08}><p className="text-base sm:text-lg text-charcoal/75 leading-relaxed">What started as a small local business grew into a full-service landscape company serving commercial and residential properties across Northern and Central California.</p></Reveal>
            <Reveal delay={0.16}><p className="text-base sm:text-lg text-charcoal/75 leading-relaxed">In 1989, Don Owens joined Nagel as an estimator and project manager. After becoming Vice President in 1993 and overseeing the company's day-to-day operations, Don and his wife, Gina, acquired the company in 2003.</p></Reveal>
            <Reveal delay={0.24}><p className="text-base sm:text-lg text-charcoal/75 leading-relaxed">Today, the company continues to build on that history — combining decades of regional experience with modern landscape design, construction and management.</p></Reveal>
            <Reveal delay={0.3}>
              <p className="font-display font-semibold tracking-[-0.02em] text-2xl sm:text-4xl text-ember pt-4">Different generation. Same standard.</p>
            </Reveal>
            <Reveal delay={0.36}>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <GrainButton testid="story-send-inquiry-button" onClick={() => openInquiry()}>
                  SEND INQUIRY
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </GrainButton>
                <button
                  data-testid="our-story-cta"
                  onClick={() => scrollToId("timeline")}
                  className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] text-charcoal border border-charcoal/20 rounded-full px-7 py-3.5 hover:bg-charcoal hover:text-cream transition-colors duration-300"
                >
                  OUR STORY
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div className="lg:sticky lg:top-28">
            <div className="relative">
              <ClipImage
                src={IMG.storyMain}
                alt="Nagel landscape garden path"
                className="aspect-[4/5] w-[82%]"
                parallax={7}
                rounded="rounded-[2rem]"
              />
              <motion.div
                initial={{ opacity: 0, y: 40, rotate: 4 }}
                whileInView={{ opacity: 1, y: 0, rotate: 3 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, delay: 0.25, ease: EASE }}
                className="absolute -bottom-12 right-0 w-[52%] rounded-[1.5rem] overflow-hidden border-[6px] border-cream shadow-[0_24px_60px_rgba(18,19,20,0.22)]"
              >
                <img src={IMG.storySmall} alt="Nagel landscape detail" loading="lazy" className="w-full aspect-square object-cover" />
              </motion.div>
              <div className="absolute -top-4 right-6 sm:right-10 rotate-[-4deg]">
                <span className="inline-block font-mono text-[9px] sm:text-[10px] tracking-[0.24em] text-ember border border-ember/40 rounded-full px-4 py-2 bg-cream/85 backdrop-blur">
                  EST. 1972 — MODESTO, CA
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="timeline" data-testid="history-timeline" className="mt-32 lg:mt-44 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-36">
            <p className="font-mono text-[10px] tracking-[0.24em] text-charcoal/40 mb-4">THE TIMELINE</p>
            <div className="relative h-24 sm:h-36 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={TIMELINE[active].year}
                  initial={{ y: "60%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  exit={{ y: "-60%", opacity: 0 }}
                  transition={{ duration: 0.55, ease: EASE }}
                  data-testid="timeline-active-year"
                  className={`font-display font-medium tracking-[-0.03em] leading-none ${
                    TIMELINE[active].year === "TODAY" ? "text-4xl sm:text-6xl text-ember" : "text-6xl sm:text-8xl text-charcoal"
                  }`}
                >
                  {TIMELINE[active].year}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="mt-6 h-px w-full bg-charcoal/10 relative">
              <motion.div
                className="absolute left-0 top-0 h-px bg-flame"
                animate={{ width: `${((active + 1) / TIMELINE.length) * 100}%` }}
                transition={{ duration: 0.6, ease: EASE }}
              />
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          {TIMELINE.map((item, i) => (
            <motion.div
              key={item.year}
              data-testid={`timeline-item-${item.year.toLowerCase()}`}
              onViewportEnter={() => setActive(i)}
              viewport={{ margin: "-45% 0px -45% 0px" }}
              className={`min-h-[38vh] flex flex-col justify-center border-l pl-8 sm:pl-12 transition-all duration-700 relative ${
                active === i ? "border-flame opacity-100" : "border-charcoal/10 opacity-35"
              }`}
            >
              <span className={`absolute -left-[5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full transition-colors duration-500 ${active === i ? "bg-flame" : "bg-charcoal/20"}`} />
              <p className="font-mono text-xs tracking-[0.24em] text-ember mb-3">{item.year}</p>
              <p className="font-display font-medium tracking-[-0.02em] text-xl sm:text-3xl text-charcoal leading-snug max-w-md">
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
